import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Box,
  tableCellClasses,
  styled,
  TableSortLabel,
  Chip
} from '@mui/material';

interface ProductProfitData {
  _id: string;
  productName: string;
  variantAttributes: { [key: string]: string };
  barcode: string;
  totalQuantity: number;
  unitName: string;
  branchName?: string;
  salesValue: number;
  cost: number;
  profit: number;
}

interface ProductProfitReportTableProps {
  data: ProductProfitData[];
  costType: 'last' | 'average';
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f5f5f5',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    fontSize: '0.95rem',
    padding: '16px 8px',
    textAlign: 'left'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.9rem',
    padding: '12px 8px',
    textAlign: 'left'
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: 'rgba(48, 195, 176, 0.08)',
    transition: 'background-color 0.2s ease'
  },
  '& > td': {
    borderBottom: '1px solid rgba(224, 224, 224, 0.5)'
  }
}));

const ProductProfitReportTable: React.FC<ProductProfitReportTableProps> = ({ data, costType }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<'profit' | 'salesValue' | 'quantity'>('profit');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = (property: 'profit' | 'salesValue' | 'quantity') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // ترتيب البيانات حسب العمود المختار
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let valueA: number;
      let valueB: number;

      switch (orderBy) {
        case 'profit':
          valueA = a.profit;
          valueB = b.profit;
          break;
        case 'salesValue':
          valueA = a.salesValue;
          valueB = b.salesValue;
          break;
        case 'quantity':
          valueA = a.totalQuantity;
          valueB = b.totalQuantity;
          break;
        default:
          valueA = a.profit;
          valueB = b.profit;
      }

      if (order === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  }, [data, orderBy, order]);

  const getVariantText = (attributes: { [key: string]: string }): string => {
    if (!attributes || typeof attributes !== 'object' || Object.keys(attributes).length === 0) {
      return '-';
    }
    
    const filteredAttributes = Object.entries(attributes)
      .filter(([key, value]) => {
        const keyLower = key.toLowerCase();
        const isBarcode = keyLower === 'barcode' || keyLower === 'باركود' || key === 'barcode';
        const hasValue = value && value.toString().trim() !== '';
        return !isBarcode && hasValue;
      })
      .map(([key, value]) => `${key}: ${value}`)
      .filter(text => text);
    
    return filteredAttributes.length > 0 ? filteredAttributes.join(' | ') : '-';
  };

  const formatCurrency = (value: number): string => {
    return value.toFixed(2);
  };

  const visibleRows = rowsPerPage === -1
    ? sortedData
    : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // حساب الإجماليات
  const totals = useMemo(() => {
    return {
      salesValue: data.reduce((sum, item) => sum + item.salesValue, 0),
      cost: data.reduce((sum, item) => sum + item.cost, 0),
      profit: data.reduce((sum, item) => sum + item.profit, 0)
    };
  }, [data]);

  return (
    <Box>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table stickyHeader aria-label="جدول ربح المنتجات">
          <TableHead>
            <TableRow>
              <StyledTableCell>الباركود</StyledTableCell>
              <StyledTableCell>اسم المنتج</StyledTableCell>
              <StyledTableCell>خصائص المتغير</StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={orderBy === 'quantity'}
                  direction={orderBy === 'quantity' ? order : 'desc'}
                  onClick={() => handleSortRequest('quantity')}
                >
                  إجمالي الكمية
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={orderBy === 'salesValue'}
                  direction={orderBy === 'salesValue' ? order : 'desc'}
                  onClick={() => handleSortRequest('salesValue')}
                >
                  قيمة المبيعات
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                التكلفة ({costType === 'last' ? 'آخر تكلفة' : 'متوسط التكلفة'})
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={orderBy === 'profit'}
                  direction={orderBy === 'profit' ? order : 'desc'}
                  onClick={() => handleSortRequest('profit')}
                >
                  الربح
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>الفرع</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length > 0 ? (
              <>
                {visibleRows.map((row, index) => (
                  <StyledTableRow key={`${row._id}-${index}`}>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {row.barcode || '-'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {row.productName}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" color="text.secondary">
                        {getVariantText(row.variantAttributes)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ color: '#30c3b0' }}
                      >
                        {row.totalQuantity} {row.unitName}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#2196f3' }}>
                        {formatCurrency(row.salesValue)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ color: '#ff9800' }}>
                        {formatCurrency(row.cost)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={formatCurrency(row.profit)}
                        size="small"
                        sx={{
                          bgcolor: row.profit >= 0 ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: '80px'
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {row.branchName || '-'}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                {/* صف الإجماليات */}
                <TableRow sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                  <StyledTableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    الإجمالي
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#2196f3' }}>
                      {formatCurrency(totals.salesValue)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#ff9800' }}>
                      {formatCurrency(totals.cost)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={formatCurrency(totals.profit)}
                      size="small"
                      sx={{
                        bgcolor: totals.profit >= 0 ? '#4caf50' : '#f44336',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: '80px'
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell></StyledTableCell>
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">لا توجد بيانات متاحة</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, { label: 'الكل', value: -1 }]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="عدد الصفوف:"
        labelDisplayedRows={
          ({ from, to, count }) => {
            if (rowsPerPage === -1) {
              return `عرض الكل (${count})`;
            }
            return `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`;
          }
        }
      />
    </Box>
  );
};

export default ProductProfitReportTable;
